import { CardData, Connection, Position, NodeType, AlertStatus, ProjectState, IProjectMetrics, MapType } from '../types';

// ---------------------------------------------------------------------------
// Configurações de projeto (TPS dinâmico) — todos os "magic numbers" centralizados.
// Permite que cada fábrica/cliente defina sua própria realidade operacional sem
// alterar a lógica de cálculo. Valores atuais reproduzem o padrão histórico
// (8h/turno, 21 dias úteis, R$50/h) quando nenhum settings é fornecido.
// ---------------------------------------------------------------------------
export interface ProjectSettings {
  /** Minutos disponíveis por turno (default: 480 = 8h) */
  shiftMinutes: number;
  /** Dias úteis por mês (default: 21) */
  workingDaysPerMonth: number;
  /** Horas trabalhadas por dia (default: 8) */
  workingHoursPerDay: number;
  /** Custo padrão por hora-recurso quando não informado no card (default: 50) */
  defaultHourlyCost: number;
  /** Limite máximo de caminhos explorados pelo motor de grafo (default: 10000) */
  maxPaths: number;
  /** Profundidade máxima de cada caminho (default: 500) */
  maxDepth: number;
}

export const DEFAULT_PROJECT_SETTINGS: ProjectSettings = {
  shiftMinutes: 480,
  workingDaysPerMonth: 21,
  workingHoursPerDay: 8,
  defaultHourlyCost: 50,
  maxPaths: 10_000,
  maxDepth: 500,
};

const resolveSettings = (settings?: Partial<ProjectSettings>): ProjectSettings => ({
  ...DEFAULT_PROJECT_SETTINGS,
  ...(settings ?? {}),
});

// ---------------------------------------------------------------------------
// Kaizen → Future projection resolver
// ---------------------------------------------------------------------------
// Resolve o valor "efetivo" de uma métrica VSM para um card, em função do
// viewMode atual. No Estado Futuro (`future_simulated`), prioriza o valor
// projetado em `vsmKaizenProjection.metrics[<key>]` (gravado pelo painel
// Kaizen). Em qualquer outro modo (ATUAL, KAIZEN), retorna o valor corrente
// gravado nos campos `vsmCT`/`vsmCO`/`vsmDisp`/`percentLivreErros`.
//
// Unidades retornadas:
//   - 'cycleTime'    → segundos (converte `vsmCT` via `vsmCTUnit`)
//   - 'setup'        → minutos  (`vsmCO`)
//   - 'availability' → percentual 0–100 (`vsmDisp`)
//   - 'quality'      → percentual 0–100 (`percentLivreErros`)
// ---------------------------------------------------------------------------
export type KaizenMetricKey = 'cycleTime' | 'setup' | 'availability' | 'quality';

const ctUnitToSeconds = (value: number, unit: unknown): number => {
  const u = typeof unit === 'string' && unit.length > 0 ? unit : 's';
  switch (u) {
    case 'dias': return value * 86400;
    case 'horas': case 'h': return value * 3600;
    case 'minutos': case 'min': return value * 60;
    case 'segundos': case 's': default: return value;
  }
};

const readCurrentMetric = (card: CardData, metric: KaizenMetricKey): number => {
  const c = card as any;
  switch (metric) {
    case 'cycleTime':
      return ctUnitToSeconds(Number(c.vsmCT) || 0, c.vsmCTUnit);
    case 'setup':
      return Number(c.vsmCO) || 0;
    case 'availability':
      return Number(c.vsmDisp ?? 100) || 0;
    case 'quality':
      return Number(c.percentLivreErros ?? 100) || 0;
  }
};

const readProjectedMetric = (card: CardData, metric: KaizenMetricKey): number | undefined => {
  const proj = (card as any).vsmKaizenProjection;
  if (!proj) return undefined;
  const slot = proj.metrics?.[metric];
  // Cycle time é gravado em segundos (`projectedSeconds`); demais usam `projectedValue`.
  const raw = metric === 'cycleTime'
    ? (slot?.projectedSeconds ?? proj.projectedSeconds)
    : slot?.projectedValue;
  if (raw == null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Retorna o valor efetivo da métrica para o card, considerando o viewMode.
 * Em `future_simulated`, usa a projeção Kaizen quando presente; caso contrário
 * cai no valor corrente. Função pura, sem efeitos colaterais.
 */
export function getEffectiveMetric(
  card: CardData,
  metric: KaizenMetricKey,
  viewMode?: string,
): number {
  if (viewMode === 'future_simulated') {
    const projected = readProjectedMetric(card, metric);
    if (projected !== undefined) return projected;
  }
  return readCurrentMetric(card, metric);
}

// ---------------------------------------------------------------------------
// Geradores de ID criptograficamente seguros (com fallback)
// ---------------------------------------------------------------------------
const cryptoRef: Crypto | undefined =
  typeof globalThis !== 'undefined' && (globalThis as any).crypto
    ? ((globalThis as any).crypto as Crypto)
    : undefined;

/** UUID v4 robusto via crypto.randomUUID; fallback usa getRandomValues; último fallback Math.random. */
export const generateUUID = (): string => {
  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID();
  }
  if (cryptoRef?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    // Per RFC 4122 §4.4
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }
  // Fallback final (ambientes legados / SSR sem crypto)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** ID curto (9 chars) para entidades locais (cards, post-its). Usa crypto quando disponível. */
export const generateId = (): string => {
  if (cryptoRef?.getRandomValues) {
    const bytes = new Uint8Array(6);
    cryptoRef.getRandomValues(bytes);
    let id = '';
    for (let i = 0; i < bytes.length; i++) id += bytes[i].toString(36).padStart(2, '0');
    return id.slice(0, 9);
  }
  return Math.random().toString(36).slice(2, 11);
};

/**
 * Duplica um estado do projeto (deep copy)
 */
export const duplicateState = (state: ProjectState): ProjectState => {
  return JSON.parse(JSON.stringify(state));
};

/**
 * Gera o próximo número sequencial para um novo card (10, 20, 30...)
 */
export const generateNextSequentialNumber = (cards: CardData[]): number => {
  const cardsWithNumbers = cards.filter(card => 
    card.numeroSequencial !== undefined && card.numeroSequencial !== null
  );
  if (cardsWithNumbers.length === 0) return 10;
  const maxNumber = Math.max(...cardsWithNumbers.map(card => card.numeroSequencial || 0));
  return maxNumber + 10;
};

/**
 * Calcula métricas avançadas de VSM.
 *
 * Refatorado para:
 *  - Travessia iterativa (DFS com pilha) — evita stack overflow em mapas grandes.
 *  - Detecção robusta de ciclos por caminho (Set local por path).
 *  - Limites de segurança (maxPaths, maxDepth) — previne travamento da UI.
 *  - Settings injetáveis (TPS dinâmico) — sem magic numbers hardcoded.
 */
export const calculateMetrics = (
  cards: CardData[],
  mapType: MapType = 'OFFICE',
  settings?: Partial<ProjectSettings>
): IProjectMetrics => {
  const cfg = resolveSettings(settings);
  void mapType; // mantido na assinatura para compatibilidade com callers existentes

  const activeCards = cards.filter(c => c.status === 'ativo' && c.tipo !== 'anotacao');
  const calculableCards = activeCards.filter(c => c.tipo !== 'card_inicial');
  const startNode = activeCards.find(c => c.tipo === 'card_inicial');
  const demand = startNode?.entrada || 0;

  let totalLTSum = 0, totalTT = 0, timeVA = 0, timeAVO = 0, timeNVA = 0, countNVA = 0;

  const processedCards = activeCards.map(c => {
    if (c.tipo === 'card_inicial') return c;
    const tpInDays = Number(c.lt) || 0, tpInMinutes = tpInDays * 24 * 60, tra = Number(c.tt) || 0;
    const pessoas = Number(c.pessoasSimultaneas) || 1, disp = (c.disponibilidade !== undefined ? c.disponibilidade : 100) / 100;
    const tpEfetivo = (disp > 0) ? tpInDays / disp : 0;
    const carga = (pessoas > 0) ? (tpInMinutes + tra) / pessoas : 0;
    return { ...c, tpEfetivo, carga };
  });

  processedCards.forEach(c => {
    if (c.tipo === 'card_inicial') return;
    const tpInMinutes = (Number(c.lt) || 0) * 24 * 60, tra = Number(c.tt) || 0;
    totalLTSum += tpInMinutes; totalTT += tra;
    if (c.tipo_atividade === 'VA') timeVA += tpInMinutes;
    else if (c.tipo_atividade === 'AVO') timeAVO += tpInMinutes;
    else { timeNVA += tpInMinutes; countNVA++; }
  });

  const totalActivities = calculableCards.filter(c => c.tipo === 'atividade' || c.tipo === 'excecao').length;
  const totalDecisions = calculableCards.filter(c => c.tipo === 'decisao').length;
  const nvaPercentage = (calculableCards.length > 0) ? (countNVA / calculableCards.length) * 100 : 0;

  let longestPath = 0, longestPathProb = 0, longestPathIds: string[] = [];
  let shortestPath = 0, shortestPathProb = 0, shortestPathIds: string[] = [];
  let longestPathTP = 0, longestPathTRA = 0, longestPathP = 0;
  let shortestPathTP = 0, shortestPathTRA = 0, shortestPathP = 0;
  let weightedAverageLeadTime = 0, hasLoop = false;
  let realBottleneck = { id: '', name: '', impact: 0, probability: 0, load: 0 };
  let maxLoad = 0;

  const cardMap = new Map<string, any>();
  processedCards.forEach(c => cardMap.set(c.id, c));

  interface CompletedPath { time: number; prob: number; pathIds: string[] }
  const completedPaths: CompletedPath[] = [];
  let totalPathVisits = 0, pathIntensityScore = 0, averagePathDepth = 0;
  let truncatedByLimit = false;

  if (startNode) {
    // ------------------------------------------------------------------
    // Travessia iterativa (DFS). Cada frame da pilha carrega o estado
    // necessário (acumulado de tempo, probabilidade e Set de ids visitados
    // para detecção de ciclo *por caminho*).
    // ------------------------------------------------------------------
    interface Frame {
      id: string;
      sum: number;
      prob: number;
      visited: Set<string>;
      pathIds: string[];
    }
    const stack: Frame[] = [{
      id: startNode.id,
      sum: 0,
      prob: 1,
      visited: new Set<string>(),
      pathIds: [],
    }];

    while (stack.length > 0) {
      if (completedPaths.length >= cfg.maxPaths) { truncatedByLimit = true; break; }
      const frame = stack.pop()!;
      const { id, sum, prob, visited, pathIds } = frame;

      if (visited.has(id)) { hasLoop = true; continue; }
      const card = cardMap.get(id);
      if (!card) continue;

      // Limite de profundidade — protege a thread em grafos patológicos.
      if (pathIds.length >= cfg.maxDepth) { truncatedByLimit = true; continue; }

      const nextVisited = new Set(visited); nextVisited.add(id);
      const nextPathIds = pathIds.concat(id);
      const cardLoad = card.status === 'removido' ? 0 : (card.carga || 0);
      const nextSum = sum + cardLoad;

      const outgoing: any[] = Array.isArray(card.conexoes_saida) ? card.conexoes_saida : [];
      if (outgoing.length === 0) {
        completedPaths.push({ time: nextSum, prob, pathIds: nextPathIds });
        for (let i = 0; i < nextPathIds.length; i++) {
          const v = cardMap.get(nextPathIds[i]);
          if (v && v.tipo !== 'card_inicial') totalPathVisits++;
        }
        continue;
      }

      for (let i = 0; i < outgoing.length; i++) {
        const conn = outgoing[i];
        let edgeProb = 1.0;
        if (card.tipo === 'decisao') {
          const raw = conn.sourceHandle === 'right' ? card.decisao_sim_pct : card.decisao_nao_pct;
          edgeProb = (Number(raw) || 0) / 100;
        }
        if (edgeProb <= 0) continue;
        stack.push({
          id: conn.targetId,
          sum: nextSum,
          prob: prob * edgeProb,
          visited: nextVisited,
          pathIds: nextPathIds,
        });
      }
    }

    if (truncatedByLimit && typeof console !== 'undefined') {
      console.warn(`[vsmGeometry.calculateMetrics] Travessia truncada (maxPaths=${cfg.maxPaths}, maxDepth=${cfg.maxDepth}). Grafo possivelmente cíclico ou explosivo.`);
    }

    if (completedPaths.length > 0) {
      const sorted = [...completedPaths].sort((a, b) => b.prob - a.prob);
      pathIntensityScore = sorted.slice(0, 3).reduce((s, p) => s + p.prob, 0) * 100;
      averagePathDepth = completedPaths.reduce((s, p) => s + p.pathIds.filter((id: string) => cardMap.get(id)?.tipo !== 'card_inicial').length, 0) / completedPaths.length;

      const maxP = completedPaths.reduce((prev, curr) => (prev.time > curr.time) ? prev : curr);
      longestPath = maxP.time; longestPathProb = maxP.prob * 100; longestPathIds = maxP.pathIds;
      maxP.pathIds.forEach((id: string) => {
        const c = cardMap.get(id);
        if (c && c.tipo !== 'card_inicial' && c.status !== 'removido') {
          longestPathTP += (Number(c.lt) || 0) * 24 * 60;
          longestPathTRA += Number(c.tt) || 0;
          longestPathP += Number(c.pessoasSimultaneas) || 1;
        }
      });

      const minP = completedPaths.reduce((prev, curr) => (prev.time < curr.time) ? prev : curr);
      shortestPath = minP.time; shortestPathProb = minP.prob * 100; shortestPathIds = minP.pathIds;
      minP.pathIds.forEach((id: string) => {
        const c = cardMap.get(id);
        if (c && c.tipo !== 'card_inicial' && c.status !== 'removido') {
          shortestPathTP += (Number(c.lt) || 0) * 24 * 60;
          shortestPathTRA += Number(c.tt) || 0;
          shortestPathP += Number(c.pessoasSimultaneas) || 1;
        }
      });

      const probSum = completedPaths.reduce((s, p) => s + p.prob, 0);
      weightedAverageLeadTime = probSum > 0
        ? completedPaths.reduce((s, p) => s + (p.time * p.prob), 0) / probSum
        : 0;

      const nodeVisitProb = new Map<string, number>();
      completedPaths.forEach(p => p.pathIds.forEach((id: string) => nodeVisitProb.set(id, (nodeVisitProb.get(id) || 0) + p.prob)));

      let maxImpact = -1;
      cardMap.forEach(card => {
        if (card.tipo === 'card_inicial' || card.status === 'removido') return;
        const prob = nodeVisitProb.get(card.id) || 0, impact = ((Number(card.lt) || 0) * 24 * 60 + (Number(card.tt) || 0)) * prob;
        if (impact > maxImpact) {
          maxImpact = impact;
          realBottleneck = { id: card.id, name: card.atividade || 'Sem nome', impact, probability: prob * 100, load: card.carga || 0 };
        }
        if ((card.carga || 0) > maxLoad) maxLoad = card.carga || 0;
      });
    }
  }

  const capacitySystem = maxLoad > 0 ? cfg.shiftMinutes / maxLoad : 0;
  const flowEfficiency = longestPath > 0 ? (totalTT / longestPath) * 100 : 0;
  let monthlyOpCost = 0;
  processedCards.forEach(c => {
    if (c.tipo === 'atividade' && c.status === 'ativo') {
      monthlyOpCost += (Number(c.tt) || 0) * (c.incomingVolume || 0) * cfg.workingDaysPerMonth * 0.8;
    }
  });

  const taktTime = demand > 0 ? cfg.shiftMinutes / demand : 0;
  const heatMapData = processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido').map(c => ({ id: c.id, name: c.atividade || 'Sem nome', people: Number(c.pessoasSimultaneas) || 1 })).sort((a, b) => b.people - a.people);

  return {
    totalLTSum, totalTT, flowEfficiency: flowEfficiency.toFixed(1), nvaPercentage: nvaPercentage.toFixed(1), totalActivities, totalDecisions, timeVA, timeAVO, timeNVA,
    longestPath: longestPath.toFixed(1), longestPathProb: longestPathProb.toFixed(1), longestPathIds, longestPathTP: longestPathTP.toFixed(1), longestPathTRA: longestPathTRA.toFixed(1), longestPathP: longestPathP.toFixed(0),
    shortestPath: shortestPath.toFixed(1), shortestPathProb: shortestPathProb.toFixed(1), shortestPathIds, shortestPathTP: shortestPathTP.toFixed(1), shortestPathTRA: shortestPathTRA.toFixed(1), shortestPathP: shortestPathP.toFixed(0),
    averageLeadTime: weightedAverageLeadTime.toFixed(1), realBottleneck, demand, capacitySystem: Math.floor(capacitySystem), backlog: Math.round(demand - capacitySystem), wipTheoretical: (longestPath * (demand / cfg.shiftMinutes)).toFixed(1), maxLoad: maxLoad.toFixed(1), monthlyOpCost, hasLoop,
    vaPercentage: (totalLTSum > 0 ? (timeVA / totalLTSum) * 100 : 0).toFixed(1), taktTime: taktTime.toFixed(1), cycleTime: longestPath.toFixed(1), taktViolation: longestPath > taktTime ? 'red' : (longestPath > taktTime * 0.9 ? 'orange' : 'green'),
    waitTimeTotal: timeNVA.toFixed(1), waitTimePercent: (totalLTSum > 0 ? (timeNVA / totalLTSum) * 100 : 0).toFixed(1), reworkRate: nvaPercentage.toFixed(1), slaTarget: (longestPath * 0.8).toFixed(1), slaAchieved: longestPath <= longestPath * 0.8, heatMapData, fragmentationPercent: (calculableCards.length > 0 ? (processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido' && (Number(c.pessoasSimultaneas) > 2 || c.conexoes_saida.length > 2)).length / calculableCards.length) * 100 : 0).toFixed(1),
    activityRanking: processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido').map(c => ({ id: c.id, name: c.atividade || 'Sem nome', totalTime: (Number(c.lt) || 0) * 24 * 60 + (Number(c.tt) || 0), percent: (totalLTSum + totalTT > 0 ? (((Number(c.lt) || 0) * 24 * 60 + (Number(c.tt) || 0)) / (totalLTSum + totalTT)) * 100 : 0) })).sort((a, b) => b.totalTime - a.totalTime).slice(0, 5),
    totalPossiblePaths: completedPaths.length, averageTimePerVisit: (totalPathVisits > 0 ? (weightedAverageLeadTime / totalPathVisits).toFixed(1) : '0'), pathComplexityPercent: (completedPaths.length > 0 ? ((completedPaths.length / Math.max(1, calculableCards.length)) * 100).toFixed(1) : '0'), pathIntensityScore: pathIntensityScore.toFixed(1), averagePathDepth: averagePathDepth.toFixed(1),
    rolledThroughputYield: (processedCards.filter(c => longestPathIds.includes(c.id) && c.tipo !== 'card_inicial' && c.tipo !== 'decisao').reduce((acc, c) => acc * ((c.percentCompleteAccurate !== undefined ? c.percentCompleteAccurate : 100) / 100), 1) * 100).toFixed(2),
    totalReworkTime: processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido').reduce((acc, c) => acc + (c.incomingVolume || 0) * (1 - ((c.percentCompleteAccurate || 100) / 100)) * (Number(c.correctionTime) || 0), 0).toFixed(1),
    hiddenFactoryCost: processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido').reduce((acc, c) => acc + ((Number(c.correctionTime) || 0) / 60) * (Number(c.hourlyCost) || 0) * (c.incomingVolume || 0) * (1 - ((c.percentCompleteAccurate || 100) / 100)), 0).toFixed(2),
    flowEfficiencyPremium: flowEfficiency.toFixed(1), averageInterruptionScore: (processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido' && c.digitalInterruptionLevel !== undefined).length > 0 ? (processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido' && c.digitalInterruptionLevel !== undefined).reduce((acc, c) => acc + (Number(c.digitalInterruptionLevel) || 0), 0) / processedCards.filter(c => c.tipo !== 'card_inicial' && c.status !== 'removido' && c.digitalInterruptionLevel !== undefined).length) : 0).toFixed(1)
  };
};

/**
 * Interface para ponto 2D
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * Interface para representar um segmento de linha ortogonal
 */
export interface OrthogonalSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  isHorizontal: boolean; // true se horizontal, false se vertical
  isTerminal?: boolean; // true se este segmento termina em uma interseção ou destino final
  intersectionPoint?: { x: number; y: number }; // Ponto de interseção se isTerminal = true
}

/**
 * Interface para métricas calculadas por nó (card)
 */
export interface NodeMetrics {
  /** Capacidade do nó (un/dia) */
  capacity: number | null;
  /** FTE Teórico necessário para processar o volume */
  fteTheoretical: number;
  /** Ocupação (FTE Teórico / Headcount Atual) */
  occupancy: number;
  /** Custo mensal estimado (R$) */
  monthlyCost: number;
  /** Tempo de Ciclo (CycleTime) em minutos */
  cycleTime: number;
  /** Volume de entrada propagado */
  incomingVolume: number;
  /** Indica se há gargalo por frequência */
  isFrequencyBottleneck?: boolean;
  /** Modo de capacidade usado */
  capacityMode?: 'time' | 'frequency';
  /** Status de alerta preditivo */
  alertStatus?: AlertStatus;
  /** Fator de carga (loadFactor) */
  loadFactor?: number;
}

// --- FUNÇÕES DE GRID E SNAP ---

export function snapToGrid(x: number, y: number, gridSize: number = 20): { x: number; y: number } {
  return {
    x: Math.round(x / gridSize) * gridSize,
    y: Math.round(y / gridSize) * gridSize,
  };
}

export function snapToGridX(x: number, gridSize: number = 20): number {
  return Math.round(x / gridSize) * gridSize;
}

export function snapToGridY(y: number, gridSize: number = 20): number {
  return Math.round(y / gridSize) * gridSize;
}

// --- FUNÇÕES DE VSM E CÁLCULOS LEAN ---

export function calculateNodeMetrics(card: CardData, settings?: Partial<ProjectSettings>): NodeMetrics {
  const cfg = resolveSettings(settings);
  const WORKING_DAYS_PER_MONTH = cfg.workingDaysPerMonth;
  const WORKING_HOURS_PER_MONTH = WORKING_DAYS_PER_MONTH * cfg.workingHoursPerDay;

  const tpInDays = Number(card.lt) || 0;
  const tpInMinutes = tpInDays * 24 * 60;
  const tra = Number(card.tt) || 0;
  const pessoasSetor = Number(card.pessoasSetor) || 1;
  const disponibilidade = (card.disponibilidade !== undefined ? card.disponibilidade : 100) / 100;
  const incomingVolume = card.incomingVolume || 0;

  // Lean Office: Disponibilidade Efetiva (recursos compartilhados)
  const workDayDuration = card.workDayDuration ?? cfg.shiftMinutes;
  const allocationPercentage = (card.allocationPercentage ?? 100) / 100;
  const effectiveAvailability = workDayDuration * allocationPercentage;

  const hasResources = card.resources && card.resources.length > 0;
  let pessoasSimultaneas = 1;
  let totalCostPerHour = 0;

  if (hasResources) {
    pessoasSimultaneas = card.resources!.reduce((sum, r) => sum + (r.quantity || 0), 0);
    totalCostPerHour = card.resources!.reduce((sum, r) => {
      const quantity = r.quantity || 0;
      const costPerHour = r.costPerHour || cfg.defaultHourlyCost;
      return sum + (quantity * costPerHour);
    }, 0);
  } else {
    pessoasSimultaneas = Number(card.pessoasSimultaneas) || 1;
    totalCostPerHour = Number(card.hourlyCost) || cfg.defaultHourlyCost;
  }

  const cycleTime = (pessoasSimultaneas > 0 && isFinite(pessoasSimultaneas))
    ? (tpInMinutes + tra) / pessoasSimultaneas
    : 0;

  const capacityMode = card.capacityMode || 'time';
  let capacity: number | null = null;
  let isFrequencyBottleneck = false;

  if (capacityMode === 'frequency') {
    const frequency = Number(card.frequencyPerDay) || 0;
    if (frequency > 0) {
      capacity = frequency;
      if (incomingVolume > frequency) isFrequencyBottleneck = true;
    }
  } else {
    if (cycleTime > 0 && isFinite(cycleTime) && disponibilidade > 0 && effectiveAvailability > 0) {
      const capacityPerMinute = (disponibilidade * pessoasSimultaneas) / cycleTime;
      capacity = capacityPerMinute * effectiveAvailability;
      if (!isFinite(capacity!) || isNaN(capacity!)) capacity = null;
    }
  }

  let effectiveCycleTimeForFTE = cycleTime;
  if (capacityMode === 'frequency') {
    const frequency = Number(card.frequencyPerDay) || 0;
    if (frequency > 0) effectiveCycleTimeForFTE = effectiveAvailability / frequency;
  }

  let fteTheoretical = 0;
  if (incomingVolume > 0 && effectiveCycleTimeForFTE > 0 && effectiveAvailability > 0) {
    fteTheoretical = (incomingVolume * effectiveCycleTimeForFTE) / effectiveAvailability;
  }

  let occupancy = 0;
  if (pessoasSetor > 0) occupancy = fteTheoretical / pessoasSetor;

  let monthlyCost = 0;
  if (hasResources && card.resources) {
    card.resources.forEach(resource => {
      const quantity = resource.quantity || 0;
      const costPerHour = resource.costPerHour || cfg.defaultHourlyCost;
      const dedication = (resource.dedication || 100) / 100;
      const effectiveCycleTimeForCost = (capacityMode === 'frequency' && card.batchExecutionTime)
        ? Number(card.batchExecutionTime) || cycleTime
        : cycleTime;

      if (quantity > 0 && costPerHour > 0 && effectiveCycleTimeForCost > 0 && incomingVolume > 0) {
        const totalTimeHours = (incomingVolume * effectiveCycleTimeForCost) / 60;
        monthlyCost += (totalTimeHours * quantity * costPerHour * dedication) * WORKING_DAYS_PER_MONTH;
      }
    });
  } else {
    if (fteTheoretical > 0 && totalCostPerHour > 0) {
      monthlyCost = fteTheoretical * totalCostPerHour * WORKING_HOURS_PER_MONTH;
    }
  }

  const alertStatus = calculatePredictiveAlert({
    incomingVolume,
    cycleTime,
    availableFte: pessoasSetor,
    shiftTime: cfg.shiftMinutes,
    capacity,
  });
  
  return {
    capacity,
    fteTheoretical,
    occupancy,
    monthlyCost,
    cycleTime,
    incomingVolume,
    isFrequencyBottleneck,
    capacityMode,
    alertStatus: alertStatus.status,
    loadFactor: alertStatus.loadFactor,
  };
}

export function calculatePredictiveAlert(params: {
  incomingVolume: number;
  cycleTime: number;
  availableFte: number;
  shiftTime: number;
  capacity: number | null;
}): { status: AlertStatus; loadFactor: number } {
  const { incomingVolume, cycleTime, availableFte, shiftTime, capacity } = params;
  if (!isFinite(incomingVolume) || !isFinite(cycleTime) || !isFinite(availableFte)) {
    return { status: 'normal', loadFactor: 0 };
  }

  let calcCapacity = (capacity !== null && capacity > 0) ? capacity : (availableFte * shiftTime) / cycleTime;
  if (!isFinite(calcCapacity) || calcCapacity <= 0) return { status: 'normal', loadFactor: 0 };

  const loadFactor = incomingVolume / calcCapacity;
  let status: AlertStatus = 'normal';
  if (loadFactor > 1.1) status = 'critical';
  else if (loadFactor > 0.9) status = 'warning';

  return { status, loadFactor };
}

export function recalculateFlow(nodes: CardData[]): CardData[] {
  const nodeMap = new Map<string, CardData>();
  nodes.forEach(node => nodeMap.set(node.id, node));
  
  const incomingConnections = new Map<string, Array<{ sourceId: string; connection: Connection }>>();
  nodes.forEach(node => {
    node.conexoes_saida.forEach(conn => {
      if (!incomingConnections.has(conn.targetId)) incomingConnections.set(conn.targetId, []);
      incomingConnections.get(conn.targetId)!.push({ sourceId: node.id, connection: conn });
    });
  });
  
  const startNode = nodes.find(n => n.tipo === 'card_inicial' && n.status === 'ativo');
  
  if (!startNode) return nodes.map(node => ({ ...node, incomingVolume: 0 }));
  
  const calculatedVolumes = new Map<string, number>();
  calculatedVolumes.set(startNode.id, startNode.entrada || 0);
  
  const dependencyCount = new Map<string, number>();
  const processed = new Set<string>();
  nodes.forEach(node => dependencyCount.set(node.id, (incomingConnections.get(node.id) || []).length));
  
  const queue: string[] = [startNode.id];
  processed.add(startNode.id);
  
  while (queue.length > 0) {
    const currentNodeId = queue.shift()!;
    const currentNode = nodeMap.get(currentNodeId);
    if (!currentNode) continue;
    
    let incomingVolume = (currentNode.tipo === 'card_inicial') ? (currentNode.entrada || 0) : 0;
    if (currentNode.tipo !== 'card_inicial') {
      (incomingConnections.get(currentNodeId) || []).forEach(({ sourceId, connection }) => {
        const sourceNode = nodeMap.get(sourceId);
        if (!sourceNode || !calculatedVolumes.has(sourceId)) return;
        let vol = calculatedVolumes.get(sourceId)!;
        if (sourceNode.tipo === 'decisao') {
          const pct = (connection.sourceHandle === 'right' ? sourceNode.decisao_sim_pct : sourceNode.decisao_nao_pct) || 50;
          vol *= (pct / 100);
        }
        incomingVolume += vol;
      });
    }
    
    calculatedVolumes.set(currentNodeId, incomingVolume);
    currentNode.conexoes_saida.forEach(conn => {
      const newDeps = Math.max(0, (dependencyCount.get(conn.targetId) || 0) - 1);
      dependencyCount.set(conn.targetId, newDeps);
      if (newDeps === 0 && !processed.has(conn.targetId)) {
        processed.add(conn.targetId);
        queue.push(conn.targetId);
      }
    });
  }
  
  return nodes.map(node => ({ ...node, incomingVolume: calculatedVolumes.get(node.id) || 0 }));
}

// --- FUNÇÕES DE GEOMETRIA ---

export function getDiamondRadius(width: number, height: number, isClassic: boolean = false): number {
  if (isClassic) return 0;
  return Math.sqrt(width * width + height * height) / 2;
}

function getDiamondDiameter(width: number, height: number, isClassic: boolean = false): number {
  return Math.sqrt(width * width + height * height);
}

function lineIntersection(p1: Point, p2: Point, p3: Point, p4: Point): Point | null {
  const denom = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
  if (denom === 0) return null;
  const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denom;
  const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denom;
  if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
    return { x: p1.x + ua * (p2.x - p1.x), y: p1.y + ua * (p2.y - p1.y) };
  }
  return null;
}

export function getDiamondConnectionPoint(center: Point, width: number, height: number, target: Point, offset: number = 0, isClassic: boolean = false): Point {
  const dx = target.x - center.x;
  const dy = target.y - center.y;
  if (dx === 0 && dy === 0) return center;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const nDx = dx / distance;
  const nDy = dy / distance;

  if (isClassic) {
    const scaleX = width / 86.561584;
    const scaleY = height / 87.134163;
    const rX = 41.5, rY = 41.3;
    const dPoints = [{ x: 0, y: -rY }, { x: rX, y: 0 }, { x: 0, y: rY }, { x: -rX, y: 0 }].map(p => ({ x: p.x * scaleX, y: p.y * scaleY }));
    
    let intersection: Point | null = null;
    let minD = Infinity;
    for (let i = 0; i < dPoints.length; i++) {
      const p1 = { x: center.x + dPoints[i].x, y: center.y + dPoints[i].y };
      const p2 = { x: center.x + dPoints[(i + 1) % dPoints.length].x, y: center.y + dPoints[(i + 1) % dPoints.length].y };
      const res = lineIntersection(center, target, p1, p2);
      if (res) {
        const d = Math.sqrt((res.x - center.x) ** 2 + (res.y - center.y) ** 2);
        if (d < minD) { minD = d; intersection = res; }
      }
    }
    if (intersection) return { x: intersection.x + (nDx * offset), y: intersection.y + (nDy * offset) };
  }
  const radius = getDiamondDiameter(width, height, isClassic) / 2;
  return { x: center.x + (nDx * (radius + offset)), y: center.y + (nDy * (radius + offset)) };
}

/**
 * Função auxiliar para garantir que um caminho SVG seja sempre ortogonal
 * Divide qualquer segmento diagonal em dois segmentos ortogonais
 */
function ensureOrthogonalPath(pathData: string): string {
  const segments = parsePathToSegments(pathData);
  if (segments.length === 0) return pathData;
  
  let result = `M ${segments[0].x1} ${segments[0].y1}`;
  segments.forEach(seg => {
    result += ` L ${seg.x2} ${seg.y2}`;
  });
  
  return result;
}

export const getConnectorPath = (startX: number, startY: number, endX: number, endY: number, sourceHandle: 'right' | 'bottom', waypoints?: Position[], obstacles?: Array<{ x: number; y: number; width: number; height: number; id: string }>) => {
  if (waypoints && waypoints.length > 0) {
    // Garantir caminho ortogonal (sem diagonais) ao conectar waypoints
    let p = `M ${startX} ${startY}`;
    let lastX = startX;
    let lastY = startY;
    
    waypoints.forEach(wp => {
      // Movimento horizontal primeiro, depois vertical (ou vice-versa) para garantir ortogonalidade
      if (Math.abs(wp.x - lastX) > 0.1) {
        p += ` L ${wp.x} ${lastY}`;
        lastX = wp.x;
      }
      if (Math.abs(wp.y - lastY) > 0.1) {
        p += ` L ${lastX} ${wp.y}`;
        lastY = wp.y;
      }
    });
    
    // Conectar ao destino de forma ortogonal
    if (Math.abs(endX - lastX) > 0.1) {
      p += ` L ${endX} ${lastY}`;
      lastX = endX;
    }
    if (Math.abs(endY - lastY) > 0.1) {
      p += ` L ${lastX} ${endY}`;
    }
    
    return ensureOrthogonalPath(p);
  }
  const GAP = 40;
  const CARD_HEIGHT = 160;
  const SAFETY_MARGIN = 30;
  const PARALLEL_VERTICAL_OFFSET = 220; // Mesma descida de um card paralelo
  
  if (sourceHandle === 'bottom') {
    // Saída bottom (ex: saída "Não" de decisão) deve descer o equivalente a um card paralelo
    // antes de virar horizontalmente para o destino
    const descendY = startY + PARALLEL_VERTICAL_OFFSET;
    // Se o destino está mais abaixo que a descida, usar a posição do destino
    const turnY = Math.max(descendY, endY);
    
    return `M ${startX} ${startY} L ${startX} ${turnY} L ${endX} ${turnY} L ${endX} ${endY}`;
  }
  if (endX < startX) { 
    // Retorno: usar getReturnLoopPath para seguir a regra correta
    return getReturnLoopPath(startX, startY, endX, endY, sourceHandle, 160, 240, obstacles);
  }
  
  // CONEXÃO HORIZONTAL (AVANÇO): origem → destino (esquerda → direita)
  const horizontalDistance = endX - startX;
  
  // Verificar se há obstáculos no caminho
  let needsDetour = false;
  let detourY = startY;
  
  if (obstacles && obstacles.length > 0) {
    // Filtrar obstáculos que estão entre origem e destino horizontalmente
    const obstaclesInPath = obstacles.filter(obs => {
      const obsLeft = obs.x;
      const obsRight = obs.x + obs.width;
      const obsTop = obs.y;
      const obsBottom = obs.y + obs.height;
      
      // Obstáculo está no caminho horizontal?
      const horizontalOverlap = obsLeft < endX && obsRight > startX;
      
      // Verificar se a linha horizontal cruzaria o obstáculo
      const minY = Math.min(startY, endY);
      const maxY = Math.max(startY, endY);
      const verticalOverlap = obsTop < maxY + SAFETY_MARGIN && obsBottom > minY - SAFETY_MARGIN;
      
      return horizontalOverlap && verticalOverlap;
    });
    
    if (obstaclesInPath.length > 0) {
      needsDetour = true;
      
      // Decidir se desviar por cima ou por baixo
      // Calcular distância para desviar por cima vs por baixo
      const topMostObstacle = Math.min(...obstaclesInPath.map(obs => obs.y));
      const bottomMostObstacle = Math.max(...obstaclesInPath.map(obs => obs.y + obs.height));
      
      const distanceToGoAbove = Math.abs(startY - (topMostObstacle - SAFETY_MARGIN));
      const distanceToGoBelow = Math.abs(startY - (bottomMostObstacle + SAFETY_MARGIN));
      
      // Preferir desviar por baixo (mais comum em VSM), exceto se for muito mais longe
      if (distanceToGoBelow < distanceToGoAbove * 1.5) {
        detourY = bottomMostObstacle + SAFETY_MARGIN;
      } else {
        detourY = topMostObstacle - SAFETY_MARGIN;
      }
    }
  }
  
  if (needsDetour) {
    // Caminho com desvio: sair horizontal → descer/subir → horizontal → subir/descer → entrar
    const turnX1 = startX + GAP / 2; // Primeiro ponto de virada (logo após sair)
    const turnX2 = endX - GAP / 2;   // Segundo ponto de virada (antes de entrar)
    
    return `M ${startX} ${startY} L ${turnX1} ${startY} L ${turnX1} ${detourY} L ${turnX2} ${detourY} L ${turnX2} ${endY} L ${endX} ${endY}`;
  }
  
  // Caminho simples (sem obstáculos)
  // Verificar se source e target estão na mesma altura
  const sameHeight = Math.abs(startY - endY) < 5;
  
  if (sameHeight) {
    // Cards na mesma linha: caminho direto horizontal
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  }
  
  // Cards em alturas diferentes: usar cotovelo no meio do vão
  const offX = horizontalDistance > GAP 
    ? startX + (horizontalDistance / 2)  // Meio do espaço se houver distância suficiente
    : startX + GAP / 2;                   // Fallback para cards muito próximos
    
  return `M ${startX} ${startY} L ${offX} ${startY} L ${offX} ${endY} L ${endX} ${endY}`;
};

export const getReturnLoopPath = (
  startX: number, 
  startY: number, 
  endX: number, 
  endY: number, 
  sourceHandle: 'right' | 'bottom', 
  cardHeight: number = 160,
  targetCardWidth: number = 240,
  obstacles?: Array<{ x: number; y: number; width: number; height: number; id: string }>
) => {
  // REGRA ESPECÍFICA DE RETORNO:
  // 1. Saem à direita do card
  // 2. Descem ABAIXO DE TODOS OS OBSTÁCULOS (não apenas 160px)
  // 3. Dobram à esquerda até a entrada do card destino
  // 4. Dobram para cima até a altura do destino
  // 5. Entram diretamente no card
  
  const HORIZONTAL_OFFSET = 20;   // Offset pequeno para sair à direita (seta desce logo após o card)
  const SAFETY_MARGIN = 100;       // Margem de segurança abaixo dos obstáculos - espaço para post-its e canal de setas
  
  // Se não for saída pela direita, usar lógica do bottom handle
  if (sourceHandle === 'bottom') {
    const dropY = startY + cardHeight; // Descer altura completa do card
    return `M ${startX} ${startY} 
            L ${startX} ${dropY} 
            L ${endX} ${dropY} 
            L ${endX} ${endY}`;
  }
  
  // REGRA DE RETORNO COMPLETA:
  // 1. Sair à direita do card (offset pequeno para descer logo após)
  const rightX = startX + HORIZONTAL_OFFSET;
  
  // 2. CRÍTICO: Calcular altura de descida considerando TODOS os obstáculos no caminho
  // Adicionar margem extra para caber post-its e canal de setas
  const POST_IT_SPACE = 80; // Espaço para post-its
  let dropY = Math.max(startY, endY) + cardHeight + POST_IT_SPACE; // Mínimo: altura de um card + espaço post-its
  
  // Se o destino está à esquerda (retorno), calcular a distância entre os dois cards
  // e descer proporcionalmente
  const horizontalDistance = Math.abs(startX - endX);
  const isShortReturn = horizontalDistance < 400; // Retorno curto (cards próximos)
  
  if (isShortReturn) {
    // Para retornos curtos, ainda manter espaço suficiente para post-its
    dropY = Math.max(startY, endY) + cardHeight / 2 + POST_IT_SPACE;
  }
  
  // Verificar todos os obstáculos que estão no caminho horizontal
  if (obstacles && obstacles.length > 0) {
    const minX = Math.min(rightX, endX);
    const maxX = Math.max(rightX, endX);
    
    // Encontrar obstáculos que estão entre a origem e o destino (horizontalmente)
    const obstaclesInPath = obstacles.filter(obs => {
      const obsRight = obs.x + obs.width;
      const obsBottom = obs.y + obs.height;
      // Obstáculo está no caminho se há sobreposição horizontal
      return obsRight > minX && obs.x < maxX;
    });
    
    // Encontrar o obstáculo mais baixo (maior Y + height)
    if (obstaclesInPath.length > 0) {
      const lowestObstacle = Math.max(...obstaclesInPath.map(obs => obs.y + obs.height));
      dropY = Math.max(dropY, lowestObstacle + SAFETY_MARGIN);
    }
  }
  
  // 3. CRÍTICO: Calcular o ponto de subida no espaço entre o último card e o destino
  // Como é retorno: startX (origem) ... cards intermediários ... endX (destino)
  
  // Encontrar o card imediatamente à ESQUERDA do destino (mais próximo)
  let cardBeforeDestination = null;
  
  if (obstacles && obstacles.length > 0) {
    // Procurar cards que terminam (x + width) ANTES ou IGUAL ao endX
    const cardsBefore = obstacles.filter(obs => {
      const obsRight = obs.x + obs.width;
      return obsRight < endX; // Card termina antes do destino
    });
    
    // Pegar o mais próximo do destino (maior x + width)
    if (cardsBefore.length > 0) {
      cardBeforeDestination = cardsBefore.reduce((closest, obs) => {
        const obsRight = obs.x + obs.width;
        const closestRight = closest.x + closest.width;
        return obsRight > closestRight ? obs : closest;
      });
    }
  }
  
  // Calcular o meio do espaço entre o card anterior e o destino
  const GAP_MARGIN = 20; // Margem de segurança
  let midX;
  
  if (cardBeforeDestination) {
    // Há um card antes: meio entre borda direita dele e borda esquerda do destino
    const leftEdge = cardBeforeDestination.x + cardBeforeDestination.width + GAP_MARGIN;
    const rightEdge = endX - GAP_MARGIN;
    midX = (leftEdge + rightEdge) / 2;
  } else {
    // Não há card no meio: usar distância fixa antes do destino
    midX = endX - 60;
  }
  
  // 4. Subir até a altura do destino
  const upY = endY;
  
  // Caminho: DIREITA → BAIXO → MEIO (esquerda) → CIMA → ENTRADA (esquerda)
  // Garantir que o último segmento sempre entre pela ESQUERDA do card
  return `M ${startX} ${startY} 
          L ${rightX} ${startY} 
          L ${rightX} ${dropY} 
          L ${midX} ${dropY} 
          L ${midX} ${upY} 
          L ${endX} ${upY}`;
};

export function parsePathToSegments(pathData: string): OrthogonalSegment[] {
  const segments: OrthogonalSegment[] = [];
  if (!pathData) return segments;
  const normalized = pathData.replace(/([MLAZ])/g, ' $1 ').trim().split(/\s+/);
  let curX = 0, curY = 0;
  for (let i = 0; i < normalized.length; i++) {
    const cmd = normalized[i];
    if (cmd === 'M') { curX = parseFloat(normalized[++i]); curY = parseFloat(normalized[++i]); }
    else if (cmd === 'L' || cmd === 'A') {
      const x = parseFloat(normalized[i + 1]);
      const y = parseFloat(normalized[i + 2]);
      if (cmd === 'A') i += 6; else i += 2;
      
      // Garantir que segmentos diagonais sejam divididos em horizontais e verticais
      const dx = Math.abs(x - curX);
      const dy = Math.abs(y - curY);
      const tolerance = 0.1;
      
      // Se ambos dx e dy são significativos, dividir em dois segmentos ortogonais
      if (dx > tolerance && dy > tolerance) {
        // Primeiro movimento horizontal, depois vertical
        segments.push({ x1: curX, y1: curY, x2: x, y2: curY, isHorizontal: true });
        segments.push({ x1: x, y1: curY, x2: x, y2: y, isHorizontal: false });
      } else {
        segments.push({ x1: curX, y1: curY, x2: x, y2: y, isHorizontal: Math.abs(curY - y) < tolerance });
      }
      
      curX = x; curY = y;
    }
  }
  return segments;
}

export function detectIntersections(pathA: string, pathB: string) {
  const segA = parsePathToSegments(pathA), segB = parsePathToSegments(pathB), inters = [];
  for (let i = 0; i < segA.length; i++) {
    for (let j = 0; j < segB.length; j++) {
      const sA = segA[i], sB = segB[j];
      if (sA.isHorizontal !== sB.isHorizontal) {
        const x = sA.isHorizontal ? sB.x1 : sA.x1, y = sA.isHorizontal ? sA.y1 : sB.y1;
        if (x >= Math.min(sA.x1, sA.x2) && x <= Math.max(sA.x1, sA.x2) && y >= Math.min(sA.y1, sA.y2) && y <= Math.max(sA.y1, sA.y2) &&
            x >= Math.min(sB.x1, sB.x2) && x <= Math.max(sB.x1, sB.x2) && y >= Math.min(sB.y1, sB.y2) && y <= Math.max(sB.y1, sB.y2)) {
          inters.push({ x, y, segmentIndexA: i, segmentIndexB: j });
        }
      }
    }
  }
  return inters;
}

export function addLineJumps(pathData: string, otherPaths: string[], radius: number = 5, mergePoints?: Set<string>) {
  if (!otherPaths.length || !pathData) return pathData;
  const inters: any[] = [];
  const segments = parsePathToSegments(pathData);
  if (segments.length === 0) return pathData;
  
  otherPaths.forEach(p => {
    const otherSegments = parsePathToSegments(p);
    detectIntersections(pathData, p).forEach(inter => {
      // Filtrar interseções que são pontos de merge (convergência)
      if (mergePoints) {
        const interKey = `${Math.round(inter.x)},${Math.round(inter.y)}`;
        if (mergePoints.has(interKey)) {
          return; // Não adicionar jump em pontos de merge
        }
      }
      
      // Verificar se é uma interseção perpendicular
      const seg = segments[inter.segmentIndexA];
      const otherSeg = otherSegments[inter.segmentIndexB];
      
      if (seg && otherSeg) {
        const isPerpendicular = 
          (seg.isHorizontal && !otherSeg.isHorizontal) || 
          (!seg.isHorizontal && otherSeg.isHorizontal);
        
        // CRÍTICO: Aplicar jump em TODAS as interseções perpendiculares
        // Também aplicar em interseções não-perpendiculares se necessário
        if (isPerpendicular) {
          inters.push({ ...inter, isHorizontal: seg.isHorizontal });
        }
      } else {
        // Se não conseguir determinar, adicionar de qualquer forma (segurança)
        inters.push({ ...inter, isHorizontal: segments[inter.segmentIndexA]?.isHorizontal });
      }
    });
  });
  
  if (!inters.length) return pathData;
  inters.sort((a, b) => a.segmentIndexA !== b.segmentIndexA ? a.segmentIndexA - b.segmentIndexA : (a.isHorizontal ? a.x - b.x : a.y - b.y));
  
  let res = `M ${segments[0].x1} ${segments[0].y1}`;
  let curX = segments[0].x1, curY = segments[0].y1;
  
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (!seg) continue;
    
    const segInters = inters.filter(inter => inter.segmentIndexA === i);
    if (!segInters.length) { 
      res += ` L ${seg.x2} ${seg.y2}`; 
      curX = seg.x2; 
      curY = seg.y2; 
      continue; 
    }
    
    segInters.forEach(inter => {
      const r = Math.min(radius, Math.abs(seg.isHorizontal ? inter.x - curX : inter.y - curY));
      
      // Garantir movimento ortogonal antes do arco
      if (seg.isHorizontal) {
        // Movimento horizontal até antes do arco
        res += ` L ${inter.x - (seg.x2 > seg.x1 ? r : -r)} ${curY}`;
      } else {
        // Movimento vertical até antes do arco
        res += ` L ${curX} ${inter.y - (seg.y2 > seg.y1 ? r : -r)}`;
      }
      
      // Adicionar arco (jump)
      res += ` a ${r} ${r} 0 0 ${seg.isHorizontal ? 0 : 1} ${seg.isHorizontal ? (seg.x2 > seg.x1 ? r * 2 : -r * 2) : 0} ${seg.isHorizontal ? 0 : (seg.y2 > seg.y1 ? r * 2 : -r * 2)}`;
      
      // Atualizar posição após o arco (garantir ortogonalidade)
      if (seg.isHorizontal) {
        curX = inter.x + (seg.x2 > seg.x1 ? r : -r);
        curY = inter.y;
      } else {
        curX = inter.x;
        curY = inter.y + (seg.y2 > seg.y1 ? r : -r);
      }
    });
    
    // Garantir movimento ortogonal até o final do segmento
    if (seg.isHorizontal) {
      res += ` L ${seg.x2} ${curY}`;
    } else {
      res += ` L ${curX} ${seg.y2}`;
    }
    
    curX = seg.x2; 
    curY = seg.y2;
  }
  
  // NÃO chamar ensureOrthogonalPath aqui porque ele remove os arcos!
  // Os arcos já foram adicionados corretamente e precisam ser preservados
  return res;
}

export function findConvergencePoint(secondary: string, trunk: string) {
  const inters = detectIntersections(secondary, trunk);
  if (inters.length === 0) {
    return null;
  }
  
  // Encontrar a interseção mais próxima do final do path secundário
  const segments = parsePathToSegments(secondary);
  if (segments.length === 0) {
    return null;
  }
  
  // Calcular distância de cada interseção até o final do path
  const intersectionsWithDistance = inters.map(intersection => {
    const seg = segments[intersection.segmentIndexA];
    if (!seg) return { ...intersection, distanceToEnd: Infinity };
    
    // Calcular distância até o final do path
    let distanceToEnd = 0;
    
    // Soma das distâncias dos segmentos após este
    for (let i = intersection.segmentIndexA + 1; i < segments.length; i++) {
      const s = segments[i];
      const dx = s.x2 - s.x1;
      const dy = s.y2 - s.y1;
      distanceToEnd += Math.sqrt(dx * dx + dy * dy);
    }
    
    // Distância dentro do segmento atual até o final do segmento
    const dx = seg.x2 - intersection.x;
    const dy = seg.y2 - intersection.y;
    distanceToEnd += Math.sqrt(dx * dx + dy * dy);
    
    return {
      ...intersection,
      distanceToEnd
    };
  });
  
  // Ordenar por distância ao final (menor distância = mais próximo do final)
  intersectionsWithDistance.sort((a, b) => a.distanceToEnd - b.distanceToEnd);
  
  // Retornar a interseção mais próxima do final (primeira após ordenação)
  const intersection = intersectionsWithDistance[0];
  return {
    x: intersection.x,
    y: intersection.y,
    segmentIndex: intersection.segmentIndexA
  };
}

export function truncatePathAtPoint(path: string, point: { x: number, y: number, segmentIndex: number }) {
  const segs = parsePathToSegments(path);
  if (segs.length === 0) return path;
  
  let res = `M ${segs[0].x1} ${segs[0].y1}`;
  
  // Adicionar segmentos completos antes do segmento de corte
  for (let i = 0; i < point.segmentIndex; i++) {
    res += ` L ${segs[i].x2} ${segs[i].y2}`;
  }
  
  // Para o segmento de corte, garantir ortogonalidade
  if (point.segmentIndex < segs.length) {
    const seg = segs[point.segmentIndex];
    // Se o ponto está no segmento, conectar ortogonalmente
    if (seg.isHorizontal) {
      // Segmento horizontal: primeiro ir até o X do ponto, depois (se necessário) até o Y
      res += ` L ${point.x} ${seg.y1}`;
      if (Math.abs(point.y - seg.y1) > 0.1) {
        res += ` L ${point.x} ${point.y}`;
      }
    } else {
      // Segmento vertical: primeiro ir até o Y do ponto, depois (se necessário) até o X
      res += ` L ${seg.x1} ${point.y}`;
      if (Math.abs(point.x - seg.x1) > 0.1) {
        res += ` L ${point.x} ${point.y}`;
      }
    }
  }
  
  return res;
}

/**
 * Calcula o layout automático de post-its vinculados a um card
 * - Até 3 post-its: lado a lado abaixo do card (centralizados)
 * - Mais de 3 post-its: todos se auto-ajustam para empilhamento uniforme
 *   mantendo a mesma largura total que 3 post-its ocupam
 */
export function calculatePostItAutoLayout(
  cardPosition: { x: number; y: number },
  cardWidth: number,
  cardHeight: number,
  postItIndex: number, // Índice do post-it (0, 1, 2, 3...)
  totalPostIts: number // Total de post-its vinculados ao card
): { x: number; y: number; relativeOffset: { x: number; y: number } } {
  const POST_IT_WIDTH = 80;
  const POST_IT_HEIGHT = 72;
  const GAP = 8; // Espaçamento entre post-its lado a lado (quando <= 3)
  const OFFSET_Y = cardHeight + 10; // Distância abaixo do card
  const STACK_OFFSET_X = 4; // Offset horizontal para empilhamento
  const STACK_OFFSET_Y = 4; // Offset vertical para empilhamento
  
  // Largura total que 3 post-its ocupam lado a lado (fixa)
  const TOTAL_WIDTH_FOR_3 = POST_IT_WIDTH * 3 + GAP * 2;
  
  // Se há 3 ou menos post-its: distribuir lado a lado normalmente
  if (totalPostIts <= 3) {
    const startX = (cardWidth - TOTAL_WIDTH_FOR_3) / 2; // Centralizar
    const x = startX + postItIndex * (POST_IT_WIDTH + GAP);
    const y = OFFSET_Y;
    
    return {
      x: cardPosition.x + x,
      y: cardPosition.y + y,
      relativeOffset: { x, y }
    };
  }
  
  // Se há mais de 3 post-its: todos se empilham uniformemente
  // mantendo a largura total de 3 post-its
  // Espaço disponível para distribuição = TOTAL_WIDTH_FOR_3 - POST_IT_WIDTH
  // Espaçamento entre cada post-it = espaço_disponível / (totalPostIts - 1)
  const availableSpace = TOTAL_WIDTH_FOR_3 - POST_IT_WIDTH;
  const spacingBetween = availableSpace / (totalPostIts - 1);
  
  // Posição X: distribuir uniformemente dentro da largura total de 3 post-its
  const startX = (cardWidth - TOTAL_WIDTH_FOR_3) / 2; // Centralizar
  const x = startX + postItIndex * spacingBetween;
  
  // Posição Y: empilhamento tipo carta (cada post-it um pouco mais abaixo e à direita)
  const y = OFFSET_Y + postItIndex * STACK_OFFSET_Y;
  
  return {
    x: cardPosition.x + x,
    y: cardPosition.y + y,
    relativeOffset: { x, y }
  };
}

export function groupConnectionsByTarget<T extends { targetId: string }>(conns: T[]) {
  const g = new Map<string, T[]>();
  conns.forEach(c => { if (!g.has(c.targetId)) g.set(c.targetId, []); g.get(c.targetId)!.push(c); });
  return g;
}

export function identifyTrunkPath<T extends { d: string }>(conns: T[]) {
  if (!conns.length) return null;
  let shortest = conns[0], minL = Infinity;
  conns.forEach(c => {
    let l = 0;
    parsePathToSegments(c.d).forEach(s => l += Math.sqrt((s.x2 - s.x1) ** 2 + (s.y2 - s.y1) ** 2));
    if (l < minL) { minL = l; shortest = c; }
  });
  return shortest;
}

export function getLastSegmentDirection(path: string) {
  const segs = parsePathToSegments(path);
  if (!segs.length) return { direction: 'unknown', lastSegment: null, endPoint: null };
  const last = segs[segs.length - 1], dx = last.x2 - last.x1, dy = last.y2 - last.y1;
  let dir: any = 'unknown';
  if (Math.abs(dx) > Math.abs(dy)) dir = dx > 0 ? 'right' : 'left'; else dir = dy > 0 ? 'down' : 'up';
  return { direction: dir, lastSegment: last, endPoint: { x: last.x2, y: last.y2 } };
}

export function calculateArrowheadOffset(p: Point, dir: string, off: number = 5) {
  if (dir === 'up') return { x: p.x, y: p.y - off };
  if (dir === 'down') return { x: p.x, y: p.y + off };
  if (dir === 'left') return { x: p.x - off, y: p.y };
  if (dir === 'right') return { x: p.x + off, y: p.y };
  return p;
}

export function truncatePathBeforePoint(path: string, point: any, off: number = 12) {
  const segs = parsePathToSegments(path), seg = segs[point.segmentIndex];
  if (!seg) return { truncatedPath: path, lineEndPoint: point };
  const dx = seg.x2 - seg.x1, dy = seg.y2 - seg.y1, l = Math.sqrt(dx * dx + dy * dy);
  const eP = { x: point.x - (dx / l) * off, y: point.y - (dy / l) * off };
  return { truncatedPath: truncatePathAtPoint(path, { ...eP, segmentIndex: point.segmentIndex }), lineEndPoint: eP };
}

export function getArrowheadRotationFromLastSegment(seg: OrthogonalSegment | null) {
  return seg ? (Math.atan2(seg.y2 - seg.y1, seg.x2 - seg.x1) * 180) / Math.PI : 0;
}

export function detectGeometricIntersections(paths: any[]) {
  const res: any[] = [], byT = new Map<string, any[]>();
  paths.forEach(p => { if (p.targetId) { if (!byT.has(p.targetId)) byT.set(p.targetId, []); byT.get(p.targetId)!.push(p); } });
  byT.forEach(group => {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const segA = parsePathToSegments(group[i].d), segB = parsePathToSegments(group[j].d);
        segA.forEach((sA, idxA) => segB.forEach((sB, idxB) => {
          if (sA.isHorizontal !== sB.isHorizontal) {
            const x = sA.isHorizontal ? sB.x1 : sA.x1, y = sA.isHorizontal ? sA.y1 : sB.y1;
            if (x >= Math.min(sA.x1, sA.x2) && x <= Math.max(sA.x1, sA.x2) && y >= Math.min(sA.y1, sA.y2) && y <= Math.max(sA.y1, sA.y2) &&
                x >= Math.min(sB.x1, sB.x2) && x <= Math.max(sB.x1, sB.x2) && y >= Math.min(sB.y1, sB.y2) && y <= Math.max(sB.y1, sB.y2)) {
              res.push({ x, y, pathId: group[i].id, segmentIndex: idxA, intersectingPathId: group[j].id, intersectingSegmentIndex: idxB });
              res.push({ x, y, pathId: group[j].id, segmentIndex: idxB, intersectingPathId: group[i].id, intersectingSegmentIndex: idxA });
            }
          }
        }));
      }
    }
  });
  return res;
}

export function splitSegmentsAtIntersections(path: string, inters: any[]) {
  const segs = parsePathToSegments(path);
  if (!inters.length) { if (segs.length) segs[segs.length - 1].isTerminal = true; return segs; }
  const res: OrthogonalSegment[] = [];
  segs.forEach((seg, idx) => {
    const sInters = inters.filter(inter => inter.segmentIndex === idx).sort((a, b) => seg.isHorizontal ? a.x - b.x : a.y - b.y);
    let start = { x: seg.x1, y: seg.y1 };
    sInters.forEach(inter => {
      res.push({ x1: start.x, y1: start.y, x2: inter.x, y2: inter.y, isHorizontal: seg.isHorizontal, isTerminal: true, intersectionPoint: inter });
      start = { x: inter.x, y: inter.y };
    });
    if (Math.abs(seg.x2 - start.x) > 1 || Math.abs(seg.y2 - start.y) > 1) {
      res.push({ x1: start.x, y1: start.y, x2: seg.x2, y2: seg.y2, isHorizontal: seg.isHorizontal, isTerminal: idx === segs.length - 1 });
    }
  });
  if (res.length) res[res.length - 1].isTerminal = true;
  return res;
}

export function rebuildPathFromSegments(segs: OrthogonalSegment[]) {
  if (!segs.length) return '';
  let p = `M ${segs[0].x1} ${segs[0].y1}`;
  segs.forEach(s => p += ` L ${s.x2} ${s.y2}`);
  return p;
}

/**
 * Aplica jumps manuais a um path SVG
 * Os jumps são pequenos arcos (semicírculos) que indicam que a linha "pula por cima" de outra
 * @param pathData - O path SVG original
 * @param manualJumps - Array de jumps com posição e tipo
 * @param radius - Raio do arco do jump (default: 5)
 * @returns Path SVG com os jumps aplicados
 */
export function applyManualJumps(pathData: string, manualJumps: Array<{ x: number; y: number; type?: 'horizontal' | 'vertical' }>, radius: number = 5): string {
  if (!manualJumps || manualJumps.length === 0 || !pathData) return pathData;
  
  const segments = parsePathToSegments(pathData);
  if (segments.length === 0) return pathData;
  
  // Para cada jump manual, encontrar o segmento mais próximo
  const jumpsWithSegments: Array<{ x: number; y: number; segmentIndex: number; isHorizontal: boolean; t: number; jumpType: 'horizontal' | 'vertical' }> = [];
  
  manualJumps.forEach(jump => {
    let minDist = Infinity;
    let bestSegment = -1;
    let bestT = 0;
    
    segments.forEach((seg, idx) => {
      // Calcular distância do ponto ao segmento
      const dx = seg.x2 - seg.x1;
      const dy = seg.y2 - seg.y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return;
      
      // Projetar o ponto no segmento
      let t = ((jump.x - seg.x1) * dx + (jump.y - seg.y1) * dy) / (len * len);
      t = Math.max(0, Math.min(1, t));
      
      const projX = seg.x1 + t * dx;
      const projY = seg.y1 + t * dy;
      const dist = Math.sqrt((jump.x - projX) ** 2 + (jump.y - projY) ** 2);
      
      if (dist < minDist) {
        minDist = dist;
        bestSegment = idx;
        bestT = t;
      }
    });
    
    if (bestSegment >= 0 && minDist < 50) { // Tolerância de 50px para "snap" ao segmento
      const seg = segments[bestSegment];
      jumpsWithSegments.push({
        x: seg.x1 + bestT * (seg.x2 - seg.x1),
        y: seg.y1 + bestT * (seg.y2 - seg.y1),
        segmentIndex: bestSegment,
        isHorizontal: seg.isHorizontal,
        t: bestT,
        jumpType: jump.type || 'horizontal' // Default: horizontal (arco para cima)
      });
    }
  });
  
  if (jumpsWithSegments.length === 0) return pathData;
  
  // Ordenar por segmento e depois por posição no segmento
  jumpsWithSegments.sort((a, b) => {
    if (a.segmentIndex !== b.segmentIndex) return a.segmentIndex - b.segmentIndex;
    return a.t - b.t;
  });
  
  // Reconstruir path com jumps
  let res = `M ${segments[0].x1} ${segments[0].y1}`;
  let curX = segments[0].x1;
  let curY = segments[0].y1;
  
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (!seg) continue;
    
    const segJumps = jumpsWithSegments.filter(j => j.segmentIndex === i);
    
    if (segJumps.length === 0) {
      res += ` L ${seg.x2} ${seg.y2}`;
      curX = seg.x2;
      curY = seg.y2;
      continue;
    }
    
    // Processar jumps no segmento
    segJumps.forEach(jump => {
      const r = radius;
      
      // Jump HORIZONTAL: arco para CIMA (usado em linhas horizontais cruzando verticais)
      if (jump.jumpType === 'horizontal') {
        // Movimento até antes do arco
        const beforeJumpX = jump.x - r;
        res += ` L ${beforeJumpX} ${curY}`;
        // Arco (semicírculo para cima) - sweep-flag=1 para arco convexo para cima
        res += ` a ${r} ${r} 0 0 1 ${r * 2} 0`;
        curX = jump.x + r;
        curY = jump.y;
      } 
      // Jump VERTICAL: arco para o LADO (usado em linhas verticais cruzando horizontais)
      else {
        // Movimento até antes do arco
        const beforeJumpY = jump.y - r;
        res += ` L ${curX} ${beforeJumpY}`;
        // Arco (semicírculo para o lado direito) - sweep-flag=0 para arco convexo para direita
        res += ` a ${r} ${r} 0 0 0 0 ${r * 2}`;
        curX = jump.x;
        curY = jump.y + r;
      }
    });
    
    // Completar até o final do segmento
    res += ` L ${seg.x2} ${seg.y2}`;
    curX = seg.x2;
    curY = seg.y2;
  }
  
  return res;
}

